import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function HaringeyCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Haringey Pothole Claims | B/C Roads RED Nearly Tripled | Fixtyer"
        description="Haringey Council's own report shows B and C roads in RED condition rose from 7.3% to 19.9% while unclassified streets hit 15.2% RED. See what the DfT AMBER rating means for your pothole claim."
        canonical="/councils/haringey"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Haringey', url: '/councils/haringey' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Haringey spends thirteen times its DfT allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN because Haringey projects £7.421m in capital spend against a £571,000 DfT allocation for 2025/26. But the overall rating is AMBER because road condition is AMBER — B and C roads in RED condition nearly tripled from 7.3% to 19.9% between comparable surveys. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on aggregate spend.',
          },
          {
            question: 'What if my pothole was on a residential street in Haringey?',
            answer: 'Unclassified roads make up 251km — 78% of Haringey\'s 322.6km borough road network. The transparency report only publishes RED-condition percentages for U-roads: 6.9% in 2021, 10.5% in 2023 and 15.2% in 2024. That is more than double in three years — roughly 38km of residential streets in RED condition at the last survey. Your claim should reference the specific road and any prior reports, not network-wide averages.',
          },
          {
            question: 'Pothole repairs fell to 1,097 in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. The fall from 2,152 potholes filled in 2023/24 to 1,097 in 2024/25 does not match the condition data, which worsened on every road class at the last comparable surveys. Haringey\'s own report states pothole numbers "will inevitably depend on the severity of the 2025/26 winter" — reactive counts fluctuate year to year while structural RED percentages kept climbing.',
          },
          {
            question: 'Is my road maintained by Haringey or Transport for London?',
            answer: 'Check before you claim. Haringey maintains borough principal roads, public roads and footways. TfL maintains London\'s red routes — including Archway Road, Seven Sisters Road, Great Cambridge Road, Bruce Grove and sections of Tottenham High Road. Haringey\'s report notes TfL has not contributed funding for planned principal-road maintenance in recent years, but defects on red routes are TfL\'s responsibility, not the borough\'s.',
          },
          {
            question: 'Does Haringey\'s risk-based prioritisation weaken my Section 58 argument?',
            answer: 'It can cut both ways. Haringey\'s transparency report describes a risk-based prioritisation process through its Highway Asset Management Strategy, and its published maintenance guidance acknowledges that higher-traffic roads may be prioritised over roads in worse condition with less traffic. That means worse-condition residential streets may wait longer for planned works. Combined with walked inspections and public reports as the main pothole-detection method, a specific prior report of your defect becomes critical evidence of actual notice.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Haringey Council"
        councilSlug="haringey"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/haringey"
        headline="Haringey Council Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Haringey</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Haringey: Nearly One in Five B and C Roads Now in RED Condition
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Haringey earns <strong className="text-emerald-400">GREEN</strong> for spend — projecting
            <strong className="text-emerald-400"> £7.4m</strong> in capital works against a
            <strong className="text-emerald-400"> £571,000</strong> DfT allocation. Yet its own transparency
            report shows <strong className="text-rose-400">RED-condition B and C roads nearly tripled</strong> from
            7.3% to 19.9% at the last comparable surveys, while unclassified residential streets climbed to
            <strong className="text-rose-400"> 15.2% RED</strong> — on a network where 251km of U-roads make up
            78% of the borough's carriageways.
          </p>

          {/* Key stat callout */}
          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">19.9%</div>
            <div className="text-white font-medium mt-2">B and C roads in RED condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 7.3% in 2021 — nearly tripled in three years. On 42.3km of B and C roads, that is
              roughly 8.4km where the council's own survey says maintenance should be considered.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Haringey's own transparency report — RED percentages rising on
            every road class at the last comparable inspections
          </p>

          {/* A-roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">A-roads (29.3km — 9% of network): deteriorating</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No survey carried out</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">5.1%</td>
                    <td className="px-4 py-2 text-center">36.4%</td>
                    <td className="px-4 py-2 text-center">58.5%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No survey carried out</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">5.8%</td>
                    <td className="px-4 py-2 text-center">42.3%</td>
                    <td className="px-4 py-2 text-center">52.1%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">14.1%</td>
                    <td className="px-4 py-2 text-center font-bold">33%</td>
                    <td className="px-4 py-2 text-center font-bold">52.9%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              A-roads in RED condition nearly tripled from 5.1% to 14.1% between comparable surveys. Green-condition
              A-roads fell from 58.5% to 52.9%. Surveys alternate years — there is no 2020, 2022 or 2025 figure.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-rose-900 mb-4">B and C roads (42.3km — 13% of network): sharply declining</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100 bg-rose-50/50">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No survey carried out</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">7.3%</td>
                    <td className="px-4 py-2 text-center">47.5%</td>
                    <td className="px-4 py-2 text-center">45.2%</td>
                  </tr>
                  <tr className="border-b border-rose-100 bg-rose-50/50">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No survey carried out</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">12.2%</td>
                    <td className="px-4 py-2 text-center">41.8%</td>
                    <td className="px-4 py-2 text-center">46.1%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">19.9%</td>
                    <td className="px-4 py-2 text-center font-bold">26.3%</td>
                    <td className="px-4 py-2 text-center font-bold">53.8%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 font-medium">
              Nearly one in five B and C roads is now in RED condition — up from one in fourteen in 2021.
              Combined RED and Amber share was 46.2% in 2024, meaning almost half the B/C network needs
              maintenance now or soon.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And This Is The Well-Funded Version</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£571k</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£7.4m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">72%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Haringey projects spending roughly <span className="text-emerald-400">thirteen times its DfT allocation</span> —
              yet RED-condition roads are climbing on every class at the last comparable surveys. The problem is not
              the chequebook alone. The network is deteriorating faster than the published condition trajectory suggests
              reactive repairs are containing it.
            </p>
          </div>
        </div>
      </section>

      {/* The 251km Residential Network */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 251km Residential Network
          </h2>
          <p className="text-surface-600 mb-8">
            78% of Haringey's borough roads are unclassified — and the council only publishes RED percentages for them
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">No survey carried out</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">6.9%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">No survey carried out</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">10.5%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">15.2%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What 15.2% Means on 251km</h3>
              <p className="text-surface-600 text-sm mb-3">
                At the 2024 survey, roughly <strong>38km of residential streets</strong> were in RED condition —
                more than double the 17km in RED at the 2021 survey. The transparency report does not publish
                Amber or Green breakdowns for U-roads, only the RED category.
              </p>
              <p className="text-surface-600 text-sm">
                Haringey's total network also includes 604km of footways and 39.3km of cycleways, plus 17,849
                streetlights, 14,276 gullies and 55 highway bridges — all maintained from the same highways budget.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Alternate-Year Gap</h3>
              <p className="text-amber-800 text-sm mb-3">
                Like several authorities, Haringey has gaps in its survey record — no U-road data for 2020 or 2022.
                For incidents in those years, there is no network-level condition benchmark for the road type that
                dominates the borough.
              </p>
              <p className="text-amber-800 text-sm">
                From 2026/27, a new PAS 2161 methodology will replace the current three-category system with five
                categories — figures before and after may not be directly comparable.
              </p>
            </div>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="As with most local highway authorities, the 'unclassified road' network – in Haringey, residential streets, rather than 'main roads' - makes up the majority of length of road to be maintained."
              source="Haringey Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing the
              condition of its roads. For Haringey's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your residential street last condition-surveyed — and was it a survey year?</li>
              <li>• If 15.2% of U-roads were RED at the last survey, what was done about yours specifically?</li>
              <li>• Does Haringey's risk-based prioritisation mean your street waited despite worse condition?</li>
              <li>• Were defects on your road identified through walked inspections or only after you reported them?</li>
            </ul>
            <p className="text-white font-medium">
              A council that knows RED percentages are climbing on residential streets cannot claim ignorance of
              systemic deterioration — only that it chose to prioritise elsewhere.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole Repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            7,208 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair counts from Haringey's own transparency report — falling in 2024/25 while condition worsened
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Cost</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">% of responsive budget</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">711</td>
                  <td className="px-6 py-4 text-center">£59,809</td>
                  <td className="px-6 py-4 text-center">3.3%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">1,520</td>
                  <td className="px-6 py-4 text-center">£127,862</td>
                  <td className="px-6 py-4 text-center">8.7%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">1,728</td>
                  <td className="px-6 py-4 text-center">£145,359</td>
                  <td className="px-6 py-4 text-center">6.7%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">2,152</td>
                  <td className="px-6 py-4 text-center">£181,026</td>
                  <td className="px-6 py-4 text-center">9.75%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">1,097</td>
                  <td className="px-6 py-4 text-center">£92,280</td>
                  <td className="px-6 py-4 text-center">3.5%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">7,208</td>
                  <td className="px-6 py-4 text-center font-bold">£606,336</td>
                  <td className="px-6 py-4 text-center">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The 2024/25 Drop</h3>
              <p className="text-surface-600 text-sm">
                Pothole fills fell from 2,152 to 1,097 — a 49% drop — while RED percentages climbed on A-roads,
                B/C roads and U-roads at the last surveys. The council attributes future pothole volumes to winter
                severity, not to structural recovery. Reactive counts and structural condition are telling different stories.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">How Potholes Are Found</h3>
              <p className="text-amber-800 text-sm">
                Haringey identifies potholes through regular walked highways inspections, public notifications,
                councillor enquiries and reports from other council officers. That makes prior reports of the same
                defect — FixMyStreet, the council's own reporting system — direct evidence of the notice the council
                says it relies on.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Financial Pressures Admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Financial Pressures Admission
          </h2>
          <p className="text-surface-600 mb-8">
            Haringey's own explanation for why DfT grants now matter — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Prior to the 2023/24 financial year, the maintenance of highway infrastructure in Haringey was entirely funded by the Council. In the last three years, the Council (because of financial pressures faced by other services) has had to reduce its capital expenditure so grant funding received from central government has been a welcomed supplement."
              source="Haringey Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Pothole repairs are carried out as reactive works and are those identified through regular walked highways inspections and additionally from notifications from the public, members enquiries and those raised by other Council officers."
              source="Haringey Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Preventative</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono">2020/21</td>
                  <td className="px-6 py-4 text-center">£0</td>
                  <td className="px-6 py-4 text-center">£4,686k</td>
                  <td className="px-6 py-4 text-center">£1,817k</td>
                  <td className="px-6 py-4 text-center">72%</td>
                  <td className="px-6 py-4 text-center">28%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono">2021/22</td>
                  <td className="px-6 py-4 text-center">£0</td>
                  <td className="px-6 py-4 text-center">£5,104k</td>
                  <td className="px-6 py-4 text-center">£1,478k</td>
                  <td className="px-6 py-4 text-center">77.5%</td>
                  <td className="px-6 py-4 text-center">22.5%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono">2022/23</td>
                  <td className="px-6 py-4 text-center">£0</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">£10,639k</td>
                  <td className="px-6 py-4 text-center">£2,175k</td>
                  <td className="px-6 py-4 text-center">83%</td>
                  <td className="px-6 py-4 text-center">17%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono">2023/24</td>
                  <td className="px-6 py-4 text-center">£176k</td>
                  <td className="px-6 py-4 text-center">£8,657k</td>
                  <td className="px-6 py-4 text-center">£1,857k</td>
                  <td className="px-6 py-4 text-center">82.4%</td>
                  <td className="px-6 py-4 text-center">17.6%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono">2024/25</td>
                  <td className="px-6 py-4 text-center">£176k</td>
                  <td className="px-6 py-4 text-center">£7,042k</td>
                  <td className="px-6 py-4 text-center">£2,613k</td>
                  <td className="px-6 py-4 text-center">73%</td>
                  <td className="px-6 py-4 text-center">27%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-mono font-bold">2025/26 (projected)</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">£571k</td>
                  <td className="px-6 py-4 text-center font-bold">£7,421k</td>
                  <td className="px-6 py-4 text-center">£2,850k</td>
                  <td className="px-6 py-4 text-center">72%</td>
                  <td className="px-6 py-4 text-center">28%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Capital Spend Has Fallen</h3>
              <p className="text-surface-600 text-sm mb-3">
                Peak capital spend was £10.639m in 2022/23. By 2024/25 it had fallen to £7.042m — a 34% reduction —
                while the council itself cites financial pressures from other services. DfT grants since 2023/24 have
                partially backfilled the gap, not replaced council investment.
              </p>
              <p className="text-surface-600 text-sm">
                Preventative maintenance share has ranged from 72% to 83% over five years — the council follows the
                Well-Maintained Highways code of practice and treats pothole repairs as reactive works.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Where the £571k Goes</h3>
              <p className="text-amber-800 text-sm mb-3">
                The 2025/26 DfT allocation targets major resurfacing on nine roads — Muswell Hill Broadway, Spur Road,
                Osborne Road, Shepherds Hill, Weir Road, Tynemouth Road, Muswell Hill Road, Spring Lane and parts of
                Alexandra Park Road. Total length: just over 1km, assessed as poor condition from annual visual surveys.
              </p>
              <p className="text-amber-800 text-sm">
                General reactive pothole work continues to be council-funded. If your road was not on that list, the
                DfT programme does not prove your carriageway received planned treatment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inspection and Prioritisation */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Surveys, Inspections and Prioritisation
          </h2>
          <p className="text-surface-600 mb-8">
            How Haringey says it finds and ranks defects — and what that means for your claim
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey Methodology</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• Annual detailed visual inspections by Metis Consultants</li>
                <li>• TfL SCANNER assessments on principal roads, shared with boroughs when published</li>
                <li>• Regular walked highways inspections for reactive pothole identification</li>
                <li>• HAMS independently audited in 2024 — awarded "Excellent" status</li>
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Prioritisation Trade-Off</h3>
              <p className="text-amber-800 text-sm mb-3">
                Haringey's maintenance guide states defects and safety are the most important factors — but also that
                a road with heavy traffic may be prioritised over a road in worse condition with less traffic. It will
                not prioritise a footway that "looks bad" if there is no trip hazard.
              </p>
              <p className="text-amber-800 text-sm">
                That is a documented policy choice: cosmetic or structural deterioration on quieter residential
                streets can wait while busier routes take precedence.
              </p>
            </div>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The risk-based prioritisation process set out in the HAMS reflects best practice to achieve maximum value for money, thereby making best use of resources and ensuring that right interventions are implemented at the most effective time."
              source="Haringey Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">TfL's Principal-Road Funding Gap</h3>
            <p className="text-brand-200 mb-4">
              Haringey maintains 29.3km of A-roads and a principal-road network including Green Lanes, West Green Road,
              High Road Wood Green and Muswell Hill Broadway. TfL used to fund principal-road maintenance through Local
              Implementation Plan bidding — but Haringey states it has received no TfL funding for planned principal-road
              maintenance in recent years.
            </p>
            <p className="text-white font-medium">
              Eight red routes — Archway Road, Seven Sisters Road, Great Cambridge Road and others — are TfL's
              responsibility entirely. Know which authority maintains your road before you file.
            </p>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Haringey invests seriously — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — projects £7.421m capital against £571k DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 72–83% preventative spend over five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> HAMS awarded "Excellent" in 2024 independent audit
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Five-year investment plan from 2025/26 with rolling resurfacing programme
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented walked inspection regime and public reporting channels
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence. Generic claims citing "potholes everywhere" will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — RED percentages rising on A, B/C and U roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> B/C roads in RED nearly tripled — 7.3% to 19.9%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 251km of U-roads at 15.2% RED — roughly 38km of residential streets
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Capital spend cut 34% since 2022/23 peak — council cites financial pressures
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Risk-based prioritisation admits worse-condition quiet roads may wait
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 7,208 potholes filled in five years — defects found through walked inspections and reports
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and an "Excellent" HAMS audit, your claim lives or dies on the
              <strong className="text-white"> specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, Haringey's reporting system) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the 15.2% RED figure and prioritisation policy are structural arguments</li>
              <li>• Whether your street was on the 1km DfT resurfacing list or the wider 5-year investment plan</li>
              <li>• Confirmation the road is borough-maintained, not a TfL red route</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and cites
              Haringey's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Haringey?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road RED trend argument</li>
                <li>• No prior-report search</li>
                <li>• No TfL red-route check</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ B/C RED rise from 7.3% to 19.9% documented</li>
                <li>✅ 251km U-road network and 15.2% RED argued</li>
                <li>✅ 7,208 potholes in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Haringey</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=haringey"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Haringey Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Haringey spends thirteen times its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN because Haringey projects £7.421m in capital spend against a
                £571,000 DfT allocation for 2025/26. But the overall rating is AMBER because road condition is AMBER —
                B and C roads in RED condition nearly tripled from 7.3% to 19.9% between comparable surveys. Section 58
                turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired,
                not on aggregate spend.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential street in Haringey?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 251km — 78% of Haringey's 322.6km borough road network. The transparency
                report only publishes RED-condition percentages for U-roads: 6.9% in 2021, 10.5% in 2023 and 15.2% in
                2024. That is more than double in three years — roughly 38km of residential streets in RED condition at
                the last survey. Your claim should reference the specific road and any prior reports, not network-wide
                averages.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs fell to 1,097 in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. The fall from 2,152 potholes filled in 2023/24 to 1,097 in 2024/25 does not match the condition
                data, which worsened on every road class at the last comparable surveys. Haringey's own report states
                pothole numbers "will inevitably depend on the severity of the 2025/26 winter" — reactive counts
                fluctuate year to year while structural RED percentages kept climbing.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Is my road maintained by Haringey or Transport for London?
              </h3>
              <p className="text-surface-600 text-sm">
                Check before you claim. Haringey maintains borough principal roads, public roads and footways. TfL
                maintains London's red routes — including Archway Road, Seven Sisters Road, Great Cambridge Road, Bruce
                Grove and sections of Tottenham High Road. Haringey's report notes TfL has not contributed funding for
                planned principal-road maintenance in recent years, but defects on red routes are TfL's responsibility,
                not the borough's.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Haringey's risk-based prioritisation weaken my Section 58 argument?
              </h3>
              <p className="text-surface-600 text-sm">
                It can cut both ways. Haringey's transparency report describes a risk-based prioritisation process
                through its Highway Asset Management Strategy, and its published maintenance guidance acknowledges that
                higher-traffic roads may be prioritised over roads in worse condition with less traffic. That means
                worse-condition residential streets may wait longer for planned works. Combined with walked inspections
                and public reports as the main pothole-detection method, a specific prior report of your defect becomes
                critical evidence of actual notice.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://haringey.gov.uk/streets-roads-travel/roadworks-closures/prioritising-planned-highways-maintenance/funding-different-types-roads"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Haringey Council Local Highways Maintenance Transparency Report (June 2025, updated September 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
